<template>
  <div :class="css">
    <div class="swiper">
      <div class="swiper-wrapper">
        <div class="swiper-slide"
             :data-index="index"
             v-for="(item, index) in items"
             :key="index"
        >
          <hero-homepage-picture
            :active="index === activeIndex"
            :translations="translations"
            :desktop-sizes="desktopSizes"
            :item="item"
            :mobile-sizes="mobileSizes"
            :is-mobile="isSmallDevice"
            :main="index === 0"
          />
          <div class="hero-homepage__container">
            <hero-homepage-content
              :cta-link-class="item.cta_link_class"
              :item="item"
            />
          </div>
        </div>
      </div>
      <div class="swiper-controls">
        <div class="swiper-controls-button swiper-button-prev" v-show="showNavigationButtons" @click="previousSlide">
          <hero-homepage-arrow direction="left"/>
        </div>
        <div class="swiper-controls-pagination swiper-pagination" v-show="showNavigationButtons"></div>
        <div class="swiper-controls-button swiper-button-next" v-show="showNavigationButtons" @click="nextSlide">
          <hero-homepage-arrow direction="right"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { merge } from "lodash-es";
import {isMobile, isSmallDevice} from "Assets/js/components/size-checker";
import HeroHomepagePicture from "./heroHomepagePicture.vue";
import HeroHomepageContent from "./heroHomepageContent.vue";
import HeroHomepageArrow from "./heroHomepageArrow.vue";

const getSwiperBundle = () => {
  return import("swiper/bundle");
};

export default {
  name: "heroHomepageGalleryV1",
  components: {
    HeroHomepagePicture,
    HeroHomepageContent,
    HeroHomepageArrow,
  },
  props: {
    desktopSizes: String,
    translations: Object,
    items: Array,
    mobileSizes: String,
  },
  emits: ["finalise", "update"],
  data() {
    return {
      useLoop: false, // Leave it as false. Using Swiper loop has some visual issues with virtual slides.
      useCustomTouch: true, // If true, it enables touch sliding on mobile devices
      activeIndex: 0, // Default: load immediately first image
      swiperModule: null,
      swiper: null, // Swiper instance
      swiperSpeed: 1000, // Swiper scrolling speed
      touchMinSwipe: 30, // Minimum difference in pixels for touch swipe
      isMobile: isMobile(),
      isSmallDevice: isSmallDevice(),
      isTouchInProgress: false, // Used by touchStart / touchEnd events
      touchOriginX: null, // Used by touchStart / touchEnd events
      resizeTimeout: null, // Used by resize() method to limit refreshes
      resizeInterval: 100, // Timeout in milliseconds. Used by resize() method to limit refreshes
    }
  },
  beforeMount() {
    // Add resize listener
    this._boundedResize = this.resize.bind(this); // Store in a var in order to remove it later
    window.addEventListener("resize", this._boundedResize);
  },
  mounted() {
    this.initSwiper();
  },
  destroyed() {
    // Remove resize listener
    window.removeEventListener("resize", this._boundedResize);
  },
  computed: {
    css() {
      return 'hero-homepage__gallery';
    },
    showNavigationButtons() {
      return this.items && this.items.length > 1;
    },
  },
  methods: {

    /**
     * Get swiper. Loads it dynamically, only first time is needed.
     * @returns {Promise<unknown>}
     */
    async getSwiper() {
      if (!this.swiperModule) {
        return getSwiperBundle()
          .then(module => {
            this.swiperModule = module.default;
          })
          .catch(e => {
            console.error('Unable to load dynamically Swiper', e);
          })
      } else {
        return Promise.resolve("resolved")
          .then(() => {
            return this.swiperModule;
          });
      }
    },

    async initSwiper(extendOptions, emitFinalise) {
      this.getSwiper()
        .then(() => {
          this.swiper = new this.swiperModule(
            this.$el.querySelector('.swiper'),
            this.swiperOptions(extendOptions, emitFinalise)
          );
        });
    },

    /**
     * Swiper options
     * @param extendOptions
     * @param emitFinalise
     * @return {any}
     */
    swiperOptions(extendOptions, emitFinalise) {
      return merge({
        loop: this.useLoop,
        pagination: {
          el: '.swiper-pagination',
          type: 'custom',
          renderCustom: this.renderBulletPagination,
          clickable: true
        },
        navigation: {
          nextEl: this.useLoop && ".swiper-button-next",
          prevEl: this.useLoop && ".swiper-button-prev",
        },
        effect: "fade",
        spaceBetween: 0,
        slidersPerView: 1,
        speed: this.swiperSpeed,
        allowTouchMove: this.useCustomTouch && this.isMobile,
        updateOnWindowResize: false,
        on: {
          init: (swiper) => {
            this.swiper = swiper;

            // Update active slide index
            this.activeSlideChange();

            if (emitFinalise !== false) {
              // Callback after first initialisation. It will process lazy CSS class and remove opacity=0
              this.$emit('finalise');
            }
          },

          /**
           * Used only if this.useLoop === false
           * @param swiper
           */
          activeIndexChange: (swiper) => {
            if (!this.useLoop) {
              this.activeSlideChange();
            }
          },

          /**
           * Used only if this.useLoop === true
           * @param swiper
           */
          realIndexChange: (swiper) => {
            if (this.useLoop) {
              this.activeSlideChange();
            }
          },

          /**
           * Touch start event
           * @param swiper
           * @param event
           */
          touchStart: (swiper, event) => {
            if (!this.useCustomTouch || !this.isMobile) {
              return;
            }

            if (this.isTouchInProgress) {
              return;
            }

            if (!event.touches) {
              return;
            }

            // Update touchInProgress flag. This is necessary to prevent activeSlideChange() to be called before touch end
            this.isTouchInProgress = true;
            this.touchOriginX = event.touches[0].clientX;
          },

          /**
           * Touch end event
           * @param swiper
           * @param event
           */
          touchEnd: (swiper, event) => {
            if (!this.useCustomTouch || !this.isMobile) {
              return;
            }

            this.touchEnd();
          },

        }
      }, extendOptions)
    },

    /**
     * Render bullet pagination.
     * @param {Swiper} swiper
     */
    renderBulletPagination(swiper) {
      const current = swiper.activeIndex;
      const className = 'swiper-pagination-bullet';
      const bullets = [];
      for (let i = 0; i < this.items.length; i++) {
        let bulletClassName = className;
        if (i === current) {
          bulletClassName += ` ${className}-active`;
        }
        bullets.push(`<span class="${bulletClassName}"><span></span></span>`);
      }
      return bullets.join('');
    },

    /**
     * Previous slide
     */
    previousSlide() {
      if (this.useLoop) {
        return;
      }
      if (this.swiper && this.items) {
        this.swiper.slideTo(this.activeIndex > 0 ? this.activeIndex - 1 : this.items.length - 1);
      }
    },

    /**
     * Next slide
     */
    nextSlide() {
      if (this.useLoop) {
        return;
      }
      if (this.swiper && this.items) {
        this.swiper.slideTo(this.activeIndex + 1 < this.items.length ? this.activeIndex + 1 : 0);
      }
    },

    /**
     * Handler for Swiper activeIndexChange/realIndexChange events. It re-aligns this.activeIndex to Swiper index
     */
    activeSlideChange() {
      if (this.isTouchInProgress) {
        return;
      }

      if (!this.useLoop) {
        this.activeIndex = this.swiper.activeIndex;
      } else {
        this.activeIndex = this.swiper.realIndex;
      }

      this.$emit('update', this.activeIndex);
    },

    /**
     * Resize callback, called by timeout set in resize() method
     */
    resizeCallback() {
      // Update flags
      this.isMobile = isMobile();
      this.isSmallDevice = isSmallDevice();

      if (this.swiper) {
        // Save current slide index
        const activeIndex = this.swiper.activeIndex;

        // Destroys and init again Swiper, retaining current slide index
        this.swiper.destroy();

        this.initSwiper({
          initialSlide: activeIndex
        }, false);
      }
    },

    /**
     * Handler for window.resize event. Using a timeout will limit refreshes during resize time.
     */
    resize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(this.resizeCallback, this.resizeInterval);
    },

    /**
     * Handler for Swiper touchEnd event
     */
    touchEnd() {
      if (!this.useCustomTouch || !this.isMobile) {
        return;
      }

      // return;
      if (this.swiper && !this.swiper.animating) {
        const touchOriginX = this.touchOriginX;

        // Reset touchInProgress flag
        this.isTouchInProgress = false;
        this.touchOriginX = null;

        const xDiff = this.swiper.touches.currentX - touchOriginX;
        // console.log('touchEnd', [touchOriginX, this.swiper.touches.currentX, xDiff]);

        if (Math.abs(xDiff) > this.touchMinSwipe) {
          if (xDiff > 0) {
            this.previousSlide();
          } else {
            this.nextSlide();
          }
        }
      }
    }
  }
}

</script>
